<template>
  <v-row class="filters--list mb-4">
    <v-col cols="12" sm="12" md="6" lg="2">
      <FilterSearch :fetchData="fetchDisputes" />
    </v-col>

    <v-col v-if="canFilterByCustomer" cols="12" sm="12" md="4" lg="2">
      <FilterAutocomplete
        :label="$t('genericFilters.label.realEstate')"
        :no-data-text="$t('appointment.filters.typeToSearchRealEstate')"
        :fetchData="fetchDisputes"
        :asyncSearch="searchCompanies"
        :loading="isCompaniesLoading"
        :items="companies"
        prepend-inner-icon="mdi-magnify"
        type="companiesIds"
        value="id"
        item-text="name"
      />
    </v-col>

    <v-col v-if="isServiceProvider" cols="12" sm="12" md="4" lg="2">
      <div class="autocomplete">
        <v-autocomplete
          @keyup="({ target }) => serviceProviderCustomerSearch(target.value)"
          @change="handleServiceProviderSelectedCustomer"
          :label="$t('genericFilters.label.realEstate')"
          :no-data-text="$t('table.noResults')"
          :items="serviceProviderCustomers"
          :loading="isSearchingForCustomers"
          prepend-inner-icon="mdi-magnify"
          item-text="name"
          item-value="company_id"
          return-object
          hide-no-data
          clearable
          outlined
          dense
        />
      </div>
    </v-col>

    <v-col v-if="canFilterByHost" cols="12" sm="12" md="4" lg="2">
      <FilterAutocomplete
        :label="$t('genericFilters.label.host')"
        :no-data-text="$t('host.label.typeToSearchHost')"
        :fetchData="fetchDisputes"
        :asyncSearch="fetchHostsBySearchText"
        :loading="isSearchingHosts"
        :items="hostSearchResults"
        prepend-inner-icon="mdi-magnify"
        type="hostId"
        value="checklist_id"
        item-text="name"
      />
    </v-col>

    <v-col cols="12" sm="12" md="4" lg="2">
      <FilterAutocomplete
        :label="$t('dispute.filters.status')"
        :fetchData="fetchDisputes"
        :items="disputeStatus"
        type="status"
      />
    </v-col>

    <v-col v-if="!isHostRoute" cols="12" sm="12" md="4" lg="2">
      <FilterAutocomplete
        :label="$t('dispute.filters.type')"
        :fetchData="fetchDisputes"
        :items="types"
        type="type"
      />
    </v-col>

    <v-col cols="12" sm="12" md="4" lg="2">
      <FilterDates
        :fetchData="fetchDisputes"
        :dateRange="true"
        :label="$t('announcement.label.list.createdAt')"
        startDateProp="startDate"
        endDateProp="endDate"
      />
    </v-col>

    <v-col cols="12" sm="12" md="4" lg="2">
      <FilterAutocomplete
        :label="$t('dispute.filters.owner')"
        :fetchData="fetchDisputes"
        :items="ownerTypeOptions"
        type="ownerType"
      />
    </v-col>

    <v-col v-if="isAdmin" cols="12" sm="12" md="4" lg="2">
      <FilterAutocomplete
        :label="$t('dispute.filters.sla')"
        :fetchData="fetchDisputes"
        :items="slaBreachedOptions"
        type="slaBreached"
      />
    </v-col>

    <v-col v-if="isAdmin" cols="12" sm="12" md="4" lg="2">
      <FilterAutocomplete
        :label="$t('dispute.filters.negligence')"
        :fetchData="fetchDisputes"
        :items="negligenceOptions"
        type="negligence"
      />
    </v-col>
  </v-row>
</template>

<script>
import FilterSearch from '@/components/filters/FilterSearch'
import FilterAutocomplete from '@/components/filters/FilterAutocomplete'
import FilterDates from '@/components/filters/FilterDates'

import { mapGetters, mapActions, mapMutations } from 'vuex'

export default {
  name: 'DisputeListFilters',

  data() {
    return {
      isSearchingForCustomers: false,
      timer: null,
    }
  },

  components: {
    FilterAutocomplete,
    FilterSearch,
    FilterDates,
  },

  computed: {
    ...mapGetters([
      'filters',
      'disputeStatus',
      'ownerTypeOptions',
      'slaBreachedOptions',
      'negligenceOptions',
      'companies',
      'isCompaniesLoading',
      'isSearchingHosts',
      'hostSearchResults',
      'serviceProviderCustomers',
    ]),

    roles() {
      const roles = localStorage.getItem('access_roles')
      if (!roles) return []
      return roles.split(',')
    },

    isAdmin() {
      return this.roles.includes('ROLE_ADMIN')
    },

    isCompany() {
      return this.roles.includes('ROLE_COMPANY')
    },

    isServiceProvider() {
      return this.roles.includes('ROLE_SERVICE_PROVIDER')
    },

    isHostRoute() {
      return this.$route?.meta?.roles?.includes('ROLE_HOST')
    },

    types() {
      if (this.isHostRoute) {
        return [
          { value: 'AUDIT', name: this.$t('dispute.type.AUDIT') },
        ]
      }

      const options = [
        { value: 'REVIEW', name: this.$t('dispute.type.REVIEW') },
        { value: 'OBJECTION', name: this.$t('dispute.type.OBJECTION') },
        { value: 'AUDIT', name: this.$t('dispute.type.AUDIT') },
      ]

      if (!this.isAdmin) {
        return options.filter(o => o.value !== 'AUDIT')
      }

      return options
    },

    canFilterByHost() {
      return this.isAdmin
    },

    canFilterByCustomer() {
      return this.isAdmin
    },
  },

  methods: {
    ...mapMutations(['setFilters']),

    ...mapActions([
      'fetchDisputes',
      'searchCompanies',
      'fetchHostsBySearchText',
      'listServiceProviderCustomers',
    ]),

    getNextFilters(params) {
      const nextFilters = { ...this.filters, ...params }

      if (this.isHostRoute) nextFilters.type = 'AUDIT'

      return nextFilters
    },

    async setCustomerIds() {
      const hasNotCustomers = this.serviceProviderCustomers.length === 0
      if (hasNotCustomers) await this.listServiceProviderCustomers()

      const companiesIds = this.serviceProviderCustomers.map(customer => customer.company_id)
      this.setFilters(this.getNextFilters({ companiesIds }))
    },

    async handleServiceProviderSelectedCustomer(value) {
      if (!value) {
        await this.setCustomerIds()
        this.fetchDisputes()
        return
      }

      this.setFilters(this.getNextFilters({ companiesIds: [value.company_id] }))
      this.fetchDisputes()
    },

    serviceProviderCustomerSearch(term) {
      if (!term || term.length <= 3) return

      clearTimeout(this.timer)
      this.setFilters(this.getNextFilters({ search: term }))

      this.timer = setTimeout(async () => {
        this.isSearchingForCustomers = true

        try {
          await this.listServiceProviderCustomers()
        } catch (error) {
          console.error(`Component DisputeListFilters. Error on listServiceProviderCustomers: ${error}`)
        } finally {
          this.isSearchingForCustomers = false
        }
      }, 1000)
    },
  },
}
</script>
